import { useCallback } from 'react';
import { postsService } from '../services/api/posts';
import { timelineService } from '../services/api/timeline';
import { usePosts } from './usePosts';
import { toBandPostUiVisibility } from '../utils/visibility';

export function useBandPosts(band, currentUser, { enabled = true, audience = 'viewer' } = {}) {
    const bandObjectId = band?.id || null;
    const timelineAudience = audience === 'public' ? 'public' : 'viewer';

    const createTimelineLoad = useCallback((options) => {
        if (!bandObjectId) {
            return Promise.resolve({
                items: [],
                hasMore: false,
                loadNextPage: async () => [],
                loadRemaining: async () => [],
                resolveAll: async () => [],
            });
        }
        return timelineService.createBandPostTimelineLoad(bandObjectId, {
            ...options,
            audience: timelineAudience,
        });
    }, [bandObjectId, timelineAudience]);

    const createPostRequest = useCallback((postData) => {
        if (!bandObjectId) {
            return Promise.reject(new Error('Band id is required to create a band post.'));
        }
        return postsService.createBandPost(bandObjectId, {
            ...postData,
            title: band?.name || postData?.title,
        });
    }, [band?.name, bandObjectId]);

    const getPostTitle = useCallback(() => (
        typeof band?.name === 'string' && band.name.trim()
            ? band.name.trim()
            : currentUser?.username
    ), [band?.name, currentUser?.username]);

    return usePosts(currentUser, {
        autoFetch: Boolean(enabled && bandObjectId),
        createTimelineLoad,
        createPostRequest,
        getPostTitle,
        attachmentDraftVisibility: 'Only me',
        normalizePostVisibilityForUi: toBandPostUiVisibility,
        resetKey: bandObjectId ? `${bandObjectId}:${timelineAudience}` : 'no-band',
    });
}

export default useBandPosts;
